import React, { useState } from "react"

import { buildMutation, compress, useMutation } from "micro-graphql-react"

import { css } from "@emotion/core"

import { ratingIcon } from "~tailwindui/helpers/classNameHelpers"

import { Loading } from "components/Icons"
import LocalTime from "components/LocalTime"
import ModalWithProvidedBody from "components/ModalWithProvidedBody"

import studentFragment from "./studentFragment"

const LessonResourceCompletion = ({ studentId, lessonResourceCompletion }) => {
  const [status, setStatus] = useState()
  const { runMutation, running } = useMutation(
    buildMutation(removeCompletionMutation)
  )

  return (
    <div key={lessonResourceCompletion.id}>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <a href={lessonResourceCompletion.session.showPath}>
          Session with {lessonResourceCompletion.session.tutor?.fullName} on{" "}
          <LocalTime
            timestamp={lessonResourceCompletion.session.startsAt}
            omitTime
          />
        </a>
        <ModalWithProvidedBody
          buttonClassName="btn btn-sm btn-danger"
          buttonText="Redo"
          modalTitle="Remove Worksheet Completion"
          afterClose={() => setStatus()}
        >
          {({ closeModal }) => (
            <>
              <div className="modal-body">
                <p>
                  Are you sure? This will remove the selected completion and
                  adjust the current worksheet for this student.
                </p>
              </div>
              <div
                className="modal-footer"
                css={css`
                  display: block !important;
                `}
              >
                {status && (
                  <div
                    className="alert alert-danger"
                    css={css`
                      padding: 9px;
                      margin-bottom: 15px;
                    `}
                  >
                    {status}
                  </div>
                )}
                <div className="d-flex align-items-center justify-content-between">
                  <button className="btn btn-danger" onClick={closeModal}>
                    Nevermind
                  </button>
                  <div>
                    <button
                      type="submit"
                      className="btn btn-info"
                      disabled={running}
                      onClick={() => {
                        runMutation({
                          studentId: studentId,
                          id: lessonResourceCompletion.id,
                        })
                          .then(
                            r => {
                              if (
                                r.removeLessonResourceCompletion.errorMessage
                              ) {
                                setStatus(
                                  r.removeLessonResourceCompletion.errorMessage
                                )
                              }
                            },
                            () =>
                              setStatus(
                                "An error occurred. Please refresh and try again."
                              )
                          )
                          .catch(() =>
                            setStatus(
                              "An error occurred. Please refresh and try again."
                            )
                          )
                      }}
                    >
                      <span>Remove It</span>
                      {running && (
                        <div
                          css={css`
                            display: inline-block;
                            margin-left: 15px;
                          `}
                        >
                          <Loading />
                        </div>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </ModalWithProvidedBody>
      </div>
      <ul className="list-unstyled my-4 flex items-center justify-between">
        {lessonResourceCompletion.studentMastery && (
          <li className="flex items-center gap-2">
            <span>Student-Perceived Mastery</span>
            {ratingIcon(lessonResourceCompletion.studentMastery)}
          </li>
        )}
        {lessonResourceCompletion.studentTopicMasteryStart && (
          <li className="flex items-center gap-2">
            <span>Student-Perceived Mastery at Topic Start</span>
            {ratingIcon(lessonResourceCompletion.studentTopicMasteryStart)}
          </li>
        )}
        {lessonResourceCompletion.studentTopicMasteryEnd && (
          <li className="flex items-center gap-2">
            <span>Student-Perceived Mastery at Topic End</span>
            {ratingIcon(lessonResourceCompletion.studentTopicMasteryEnd)}
          </li>
        )}
        {lessonResourceCompletion.tutorMastery && (
          <li className="flex items-center gap-2">
            <span>Tutor-Perceived Mastery</span>
            {ratingIcon(lessonResourceCompletion.tutorMastery)}
          </li>
        )}
        {lessonResourceCompletion.tutorTopicMastery && (
          <li>
            <span>Tutor-Perceived Topic Mastery</span>
            {ratingIcon(lessonResourceCompletion.tutorTopicMastery)}
          </li>
        )}
      </ul>
      <hr />
    </div>
  )
}

const removeCompletionMutation = compress`
  ${studentFragment}
  mutation($id: ID!, $studentId: ID!) {
    removeLessonResourceCompletion(id: $id) {
      errorMessage
      removedLessonResourceCompletion {
        id
        student {
          ...StudentLessonFields
        }
      }
    }
  }
`

export default LessonResourceCompletion
